import moment from "moment";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import { Calendar as BigCalendar, Views, DateLocalizer, momentLocalizer } from 'react-big-calendar'
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useCalendar } from "./CalendarContext";
import { useHistory } from "react-router-dom";
import { Form, Modal, Popover, Tooltip, Typography, Checkbox, Button, Input, DatePicker } from "antd";
import { t } from "i18next";
import { BrowserView, MobileView, isMobile } from 'react-device-detect'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css'
import './calendar.css'
import ApiService from "../../../core/service/ApiService";
import Helper from "../../../core/Helper";
import { PERMISSIONS, useUser } from "../../../core/context/UserContext";
import { Currency } from "../../Payment/MultiplePaymentApp";
import { ConsoleSqlOutlined } from "@ant-design/icons";

const localizer = momentLocalizer(moment)
const DnDCalendar = withDragAndDrop(BigCalendar);

const colors = [
    { color: '#3174ad', text: 'NO-CHECK-IN-EVENT' },
    { color: '#3498db', text: 'SELECTED-EVENT' },
    { color: '#9b59b6', text: 'SAME-PATIENT-EVENT' },
    { color: '#6ab04c', text: 'CHECK-IN-EVENT' },
]

export default function Calendar({ date, time, onSelect, height }) {
    const history = useHistory();
    const { setSelectedAppointment, selectedAppointment, units, filter, refresh, changedState, setSelectedUnits } = useCalendar();
    const { firmSettings } = useUser();
    const [limits, setLimits] = useState({})
    const [resources, setResources] = useState([])
    const { hasPermission, user, formatPrice } = useUser();

    const [appointments, setAppointments] = useState([]);
    const [editingBlockedSlot, setEditingBlockedSlot] = useState(null);

    useEffect(() => {
        setLimits(getMinMaxDate())
    }, [firmSettings])

    const getAppointments = async () => {
        if (!filter.selectedUnits || filter.selectedUnits.length === 0)
            return;

        if (!filter.date)
            return;

        const res = await ApiService.Appointment.GetAppointmentList({
            appointmentDate: Helper.Date.beginOfDay(filter.date),
            unitIdList: filter.selectedUnits
        });

        const blockedRes = await ApiService.Appointment.GetBlockedSlots({
            date: Helper.Date.beginOfDay(filter.date),
            unitIds: filter.selectedUnits
        });

        const blockedSlots = (blockedRes.success ? blockedRes.data : []).map(slot => ({
            id: slot.id,
            title: slot.description || t('Blocked Slot'),
            start: new Date(moment(slot.startDate).local().toDate()),
            end: new Date(moment(slot.endDate).local().toDate()),
            resourceId: slot.unitId,
            allDay: false,
            isBlocked: true,
            isDeleted: false,
            stateGroupId: -1 // Special group for blocked
        }));

        if (res.success) {
            const apps = res.data.map(app => {
                let startDate = moment(new Date(app.startDate));
                let endDate = moment(new Date(app.endDate));

                if (startDate.format('YYYY-MM-DD') === moment().format('YYYY-MM-DD')) {
                    endDate = moment(new Date(app.endDate)).isAfter(moment().endOf('day')) ? moment().endOf('day') : moment(new Date(app.endDate));
                }

                startDate = startDate.toDate();
                endDate = endDate.toDate();

                if (startDate.getTime() === endDate.getTime()) {
                    endDate.setMinutes(endDate.getMinutes() + 15);
                }

                let _end = endDate
                let _start = startDate

                if (startDate.getTime() > endDate.getTime()) {
                    _end = startDate
                    _start = endDate
                }

                const priceText = app.totalPrice === -1
                    ? null
                    : formatPrice(app.totalPrice, { currency: app.currency });

                return {
                    ...app,
                    id: app.appointmentId,
                    nameText: app.title,
                    priceText,
                    title: priceText ? `${app.title} - ${priceText}` : `${app.title}`,
                    start: _start,
                    end: _end,
                    resourceId: app.unitId,
                    allDay: false,
                }
            })

            setAppointments([...apps, ...blockedSlots]);
        } else {
            setAppointments(blockedSlots);
        }
    }

    const handleSelectSlot = (select) => {
        const isPastDate = Helper.Date.isBeforeToday(select.start);
        const isPermission = hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT);
        if (isPastDate && !isPermission) {
            alert("Geçmiş tarihlerde randevu oluşturama yetkiniz bulunmamaktadır");
            return;
        }

        const isPermission_create_edit = hasPermission(PERMISSIONS.APPOINTMENT_CREATE_EDIT);
        if (!isPermission_create_edit) {
            alert("Randevu oluşturama yetkiniz bulunmamaktadır.");
            return;
        }

        setSelectedAppointment({ appointmentId: null })
        const unit = units.find(x => x.id === select.resourceId)
        if (!unit)
            alert('Birim bulunamadi !!')

        let description = "";

        Modal.confirm({
            title: `${t('New Appointment')}, ${unit?.firm?.name}` + ` - ${unit.name}`,
            content: (
                <>
                    <Typography.Text>
                        {moment(select.start).format('DD/MM/yyyy')}, {t(moment(select.start).format('dddd'))}
                        <br />
                        {moment(select.start).format('HH:mm')}
                        {' - '}
                        {moment(select.end).format('HH:mm')}
                    </Typography.Text>
                    <div style={{ marginTop: 10 }}>
                        <Input
                            placeholder={t("Description (Optional)")}
                            style={{ marginBottom: 8 }}
                            onChange={(e) => description = e.target.value} // Capture input value
                        />
                        <Button type="dashed" danger block onClick={() => {
                            Modal.destroyAll();
                            handleCloseSlot(select, unit, description);
                        }}>
                            {t('Block Slot')}
                        </Button>
                    </div>
                </>
            ),
            okText: t('Continue'),
            cancelText: t('Cancel'),
            onOk: () => {
                const params = [
                    `unitId=${select.resourceId}`,
                    `unitName=${unit.name}`,
                    `firmId=${unit?.firmId}`,
                    `firmName=${unit?.firm?.name}`,
                    `startDate=${moment(select.start).format('yyyyMMDDHHmm')}`,
                    `endDate=${moment(select.end).format('yyyyMMDDHHmm')}`,
                ]

                if (onSelect) {
                    onSelect({
                        unitId: `${select.resourceId}`,
                        unitName: `${unit.name}`,
                        firmId: `${unit?.firmId}`,
                        firmName: `${unit?.firm?.name}`,
                        startDate: `${moment(select.start).format('yyyyMMDDHHmm')}`,
                        endDate: `${moment(select.end).format('yyyyMMDDHHmm')}`,
                    })
                }
                else {
                    history.push(`/app/patients/appointments?${params.join('&')}`)
                }
            }
        })
    }

    const handleCloseSlot = async (select, unit, description) => {
        // description argumani handleSelectSlot'tan geliyor
        const res = await ApiService.Appointment.BlockSlot({
            unitId: select.resourceId,
            startDate: select.start,
            endDate: select.end,
            description: description
        });

        if (res.success) {
            refresh();
        }
    }

    const resizeEvent = async ({ event, start, end }) => {
        if (event.isBlocked) {
            const res = await ApiService.Appointment.UpdateBlockedSlot({
                id: event.id,
                startDate: start,
                endDate: end,
                description: event.title === t('Blocked Slot') ? null : event.title
            });
            if (res.success) refresh();
            return;
        }

        if (event.id) {
            const res = await ApiService.Appointment.EditAppointmentDate(event.id, {
                startDate: start,
                endDate: end
            })

            if (res.success) {
                refresh()
            }
        }
    }

    const onSelectEvent = (calEvent) => {

        if (calEvent.isBlocked) {
            setEditingBlockedSlot(calEvent);
            return;
        }

        const appointment_manager_permission = hasPermission(PERMISSIONS.APPOINTMENT_VIEW_OTHER_USER);
        if (!appointment_manager_permission && calEvent.responsibleUserId != user.id) {
            alert(t("You do not have permission to view the patient card."));
            return;
        }
        if (selectedAppointment.appointmentId !== calEvent.id) {
            setSelectedAppointment({
                ...calEvent,
                appointmentId: calEvent.id,
                patientId: calEvent.patientId,
                unitId: calEvent.resourceId,
            })
        } else {
            setSelectedAppointment({ appointmentId: null, patientId: null, unitId: null })
        }

        return true;
    }

    const moveEvent = async ({ event, start, end, resourceId, }) => {
        if (event.isBlocked) {
            const res = await ApiService.Appointment.UpdateBlockedSlot({
                id: event.id,
                startDate: start,
                endDate: end,
                description: event.title === t('Blocked Slot') ? null : event.title
            });
            if (res.success) refresh();
            return;
        }
        if (event.id) {
            const res = await ApiService.Appointment.EditAppointmentDate(event.id, {
                startDate: start,
                endDate: end,
                unitId: resourceId
            })

            if (res.success) {
                refresh()
            }
        }
    }

    function shadeColor(color, percent) {

        var R = parseInt(color.substring(1, 3), 16);
        var G = parseInt(color.substring(3, 5), 16);
        var B = parseInt(color.substring(5, 7), 16);

        R = parseInt(R * (100 + percent) / 100);
        G = parseInt(G * (100 + percent) / 100);
        B = parseInt(B * (100 + percent) / 100);

        R = (R < 255) ? R : 255;
        G = (G < 255) ? G : 255;
        B = (B < 255) ? B : 255;

        var RR = ((R.toString(16).length == 1) ? "0" + R.toString(16) : R.toString(16));
        var GG = ((G.toString(16).length == 1) ? "0" + G.toString(16) : G.toString(16));
        var BB = ((B.toString(16).length == 1) ? "0" + B.toString(16) : B.toString(16));

        return "#" + RR + GG + BB;
    }

    const getEventColor = (event) => {
        let color = event.stateColor

        if (event.isDeleted) {
            color = '#cacaca'
        }
        else if (event.isBlocked) {
            color = '#808080';
        }
        else if (event.id === selectedAppointment.appointmentId) {
            color = shadeColor(event.stateColor, 10);
        }

        return color
    }

    const { defaultDate, formats, scrollToTime } = useMemo(
        () => ({
            defaultDate: date || new Date(),
            scrollToTime: time || new Date(),
            formats: {
                selectRangeFormat: ({
                    start,
                    end
                }, culture, localizer) => localizer.format(start, 'HH:mm', culture) + ' - ' + localizer.format(end, 'HH:mm', culture),
                timeGutterFormat: (date, culture, localizer) =>
                    localizer.format(date, 'HH:mm', culture),
                dayHeaderFormat: (date, culture, localizer) =>
                    localizer.format(date, 'DD/MM/yyyy', culture),
                eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
                    localizer.format(start, 'HH:mm', culture) +
                    ' - ' +
                    localizer.format(end, 'HH:mm', culture),
            }
        }),
        []
    )

    useEffect(() => {
        refresh()
    }, [filter])

    useEffect(() => {
        if (changedState > 1) {
            setSelectedAppointment({})
            getAppointments();
        }
    }, [changedState])

    const getMinMaxDate = () => {
        let settings = firmSettings;
        if (settings) {
            return {
                min: new Date(1972, 1, 1, settings.startTime.split(':')[0], settings.startTime.split(':')[1]),
                max: new Date(1972, 1, 1, settings.endTime.split(':')[0], settings.endTime.split(':')[1]),
            }
        }

        return {
            min: new Date(1972, 1, 1, 8, 0),
            max: new Date(1972, 1, 1, 23, 30),
        }
    }

    const { step, max, min } = useMemo(() => ({
        step: 15,
        min: getMinMaxDate().min,
        max: getMinMaxDate().max
    }), [])

    const isMultipleFirmSelected = (unitIdList) => {
        return Array.from(new Set(unitIdList.map(x => units.find(y => y.id === x)?.firmId))).length > 1;
    }

    useEffect(() => {
        const selected = filter.selectedUnits.filter(x => units.find(y => y.id === x));
        const multipleFirm = isMultipleFirmSelected(selected);

        const resources = selected.map(x => {
            const u = units.find(y => y.id === x);
            return {
                resourceId: u.id,
                resourceName: multipleFirm ? `${u?.firm?.name} - ${u.name}` : u.name,
            }
        });

        setResources(resources)
    }, [filter.selectedUnits])

    useEffect(() => {
        const selected = filter.selectedUnits.filter(x => units.find(y => y.id === x));
        const multipleFirm = isMultipleFirmSelected(selected);

        setResources(selected.map(x => {
            const u = units.find(y => y.id === x);
            return {
                resourceId: u.id,
                resourceName: multipleFirm ? `${u?.firm?.name} - ${u.name}` : u.name,
            }
        }))
    }, [units])

    const filteredAppointments = useMemo(() => {
        if (!filter.hideDeletedAndCancelled) return appointments;
        return appointments.filter(x => (!x.isDeleted && x.stateGroupId !== 3) || x.isBlocked);
    }, [appointments, filter.hideDeletedAndCancelled]);

    // --- Desktop: sütun (birim) başlıklarını drag-drop ile yeniden sırala ---
    // Sütun sırası `resources` dizisinin (= filter.selectedUnits) sırasına bağlı.
    // Başlık bırakıldığında selectedUnits yeniden sıralanıp persist ediliyor
    // (setSelectedUnits zaten localStorage'a yazıyor). Sadece desktopta aktif.
    const dragUnitIdRef = useRef(null);
    const [dragOverUnitId, setDragOverUnitId] = useState(null);
    // reorderUnits stale closure'a düşmesin diye güncel sırayı ref'te tutuyoruz.
    const selectedUnitsRef = useRef(filter.selectedUnits);
    selectedUnitsRef.current = filter.selectedUnits;

    const reorderUnits = (fromId, toId) => {
        if (fromId == null || toId == null || fromId === toId) return;
        const order = [...selectedUnitsRef.current];
        const fromIdx = order.indexOf(fromId);
        const toIdx = order.indexOf(toId);
        if (fromIdx === -1 || toIdx === -1) return;
        order.splice(fromIdx, 1);
        order.splice(toIdx, 0, fromId);
        setSelectedUnits(order);
    };

    const DesktopResourceHeader = React.useCallback(({ label, resource }) => {
        const id = resource.resourceId;
        const isDragOver = dragOverUnitId === id;
        return (
            <div
                draggable
                onDragStart={(e) => {
                    dragUnitIdRef.current = id;
                    e.dataTransfer.effectAllowed = 'move';
                    try { e.dataTransfer.setData('text/plain', String(id)); } catch (_) { }
                }}
                onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    setDragOverUnitId((prev) => (prev === id ? prev : id));
                }}
                onDragLeave={() => setDragOverUnitId((prev) => (prev === id ? null : prev))}
                onDrop={(e) => {
                    e.preventDefault();
                    reorderUnits(dragUnitIdRef.current, id);
                    dragUnitIdRef.current = null;
                    setDragOverUnitId(null);
                }}
                onDragEnd={() => { dragUnitIdRef.current = null; setDragOverUnitId(null); }}
                title={t('Drag to reorder columns')}
                style={{
                    cursor: 'grab',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    padding: '0 4px',
                    background: isDragOver ? 'rgba(24,144,255,0.12)' : undefined,
                    boxShadow: isDragOver ? 'inset 3px 0 0 #1890ff' : undefined,
                }}
            >
                <span style={{ marginRight: 4, opacity: 0.45 }}>⋮⋮</span>{label}
            </div>
        );
    }, [dragOverUnitId]);

    const desktopCalendarComponents = useMemo(
        () => ({ resourceHeader: DesktopResourceHeader }),
        [DesktopResourceHeader]
    );

    // Mobilde event üzerinde long-press (350ms) ile gesture aktifleşir.
    // Dokunma konumu event genişliğine göre mod belirler:
    //   sağ %25  → MOVE
    //   sol %75  → RESIZE
    // Long-press öncesi parmak 10px+ hareket ederse iptal → native scroll devam eder.
    // Slot seçimi ve event tap (onSelectEvent) BigCalendar'ın kendi davranışıyla çalışır.
    const mobileRootRef = useRef(null);
    const [touchPreview, setTouchPreview] = useState(null);
    const gestureRef = useRef(null);
    // event.id -> .rbc-event DOM elementi
    const eventDomMapRef = useRef(new Map());

    // Mobil event içeriği: parent .rbc-event'i data-rbc-event-id ile etiketler.
    // (eventPropGetter data-* attribute'larını .rbc-event üzerine yaymıyor, bu yüzden
    //  components.event ile içeriden işaretliyoruz.)
    const MobileEventInner = React.useCallback(function MobileEventInner({ event, title }) {
        const ref = useRef(null);
        useEffect(() => {
            const el = ref.current?.closest('.rbc-event');
            if (!el) return;
            const id = String(event.id);
            el.setAttribute('data-rbc-event-id', id);
            eventDomMapRef.current.set(id, el);
            return () => {
                if (eventDomMapRef.current.get(id) === el) {
                    eventDomMapRef.current.delete(id);
                }
            };
        });
        // Dar mobil sütunlarda tek satır kırpıldığında fiyat ellipsis'in arkasında
        // kalıyordu. İsim kırpılabilir (flex: 1, minWidth: 0), fiyat kırpılmaz.
        return (
            <div ref={ref} style={{ display: 'flex', gap: 3, overflow: 'hidden', whiteSpace: 'nowrap' }}>
                <span style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {event.nameText ?? title}
                </span>
                {event.priceText && (
                    <span style={{ flex: '0 0 auto', fontWeight: 600 }}>
                        {event.priceText}
                    </span>
                )}
            </div>
        );
    }, []);

    // components prop'unu memoize et — her render'da yeni obje vermeyelim;
    // aksi halde BigCalendar event'leri remount edebilir (gesture sırasında
    // DOM kaybı = "eski haline döndü" görüntüsü).
    const mobileCalendarComponents = useMemo(() => ({ event: MobileEventInner }), [MobileEventInner]);

    // hasPermission, useUser() her çağrıda yeni fonksiyon referansı döndüğü için
    // useEffect deps'inde tutulamaz (her setTouchPreview re-render'ında cleanup
    // çalışır, gesture kaybolur). Stable ref ile en güncel sürüme erişiyoruz.
    const hasPermissionRef = useRef(hasPermission);
    hasPermissionRef.current = hasPermission;

    // Mobil "move" gesture'ında parmağın üzerinde olduğu birim sütununu (resource)
    // belirlemek için güncel resources sırasına stale closure olmadan erişiyoruz.
    const resourcesRef = useRef(resources);
    resourcesRef.current = resources;

    useEffect(() => {
        if (!isMobile) return;
        if (!hasPermissionRef.current(PERMISSIONS.APPOINTMENT_CREATE_EDIT)) return;
        const root = mobileRootRef.current;
        if (!root) return;

        const SNAP_MIN = 15;
        const MIN_DURATION_MS = SNAP_MIN * 60 * 1000;
        const LONG_PRESS_MS = 350;
        const MOVE_TOLERANCE_PX = 10;

        const snap = (ms) => Math.round(ms / (SNAP_MIN * 60 * 1000)) * (SNAP_MIN * 60 * 1000);
        const fmtRange = (s, e) =>
            `${moment(s).format('HH:mm')} - ${moment(e).format('HH:mm')}`;

        let pending = null;
        const clearPending = () => {
            if (pending && pending.timer) clearTimeout(pending.timer);
            pending = null;
        };

        const activate = () => {
            if (!pending) return;
            const { appointment, eventEl, rect, msPerPx, startY, mode } = pending;
            const origStart = new Date(appointment.start).getTime();
            const origEnd = new Date(appointment.end).getTime();
            gestureRef.current = {
                mode,
                appointment,
                eventEl,
                origStart,
                origEnd,
                rectTop: rect.top,
                rectBottom: rect.bottom,
                rectLeft: rect.left,
                rectWidth: rect.width,
                rectHeight: rect.height,
                msPerPx,
                initialTouchY: startY,
                newStart: origStart,
                newEnd: origEnd,
                origResourceId: appointment.resourceId,
                newResourceId: appointment.resourceId,
            };
            pending = null;
            setTouchPreview({
                mode,
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
                label: fmtRange(origStart, origEnd),
                activated: true,
            });
        };

        const onTouchStart = (e) => {
            if (e.touches.length !== 1) return;
            const eventEl = e.target.closest && e.target.closest('.rbc-event');
            if (!eventEl) return;
            const id = eventEl.getAttribute('data-rbc-event-id');
            if (!id) return;
            const appointment = filteredAppointments.find(a => String(a.id) === id);
            if (!appointment || appointment.isBlocked) return;

            // BigCalendar Selection'ın bubble handler'ına ulaşmasın diye propagation
            // durduruluyor. ÖNEMLİ: touchstart'ta preventDefault ÇAĞIRMIYORUZ — aksi
            // halde tarayıcı tüm touch dizisi için native scroll'u iptal eder ve
            // kart üzerinden başlayan yatay kaydırma çalışmaz. Gesture gerçekten
            // aktive olduğunda (onTouchMove içinde) preventDefault ediyoruz.
            // Context menu / text selection ise contextmenu handler'ı + CSS
            // (user-select/touch-callout: none) ile zaten engelleniyor.
            e.stopPropagation();

            const rect = eventEl.getBoundingClientRect();
            const durMs = new Date(appointment.end).getTime() - new Date(appointment.start).getTime();
            const msPerPx = durMs / Math.max(rect.height, 1);

            // Sağ %25 → MOVE, sol %75 → RESIZE
            const touchX = e.touches[0].clientX;
            const mode = (touchX - rect.left) > rect.width * 0.75 ? 'move' : 'resize';

            clearPending();
            pending = {
                eventEl,
                appointment,
                rect,
                msPerPx,
                mode,
                startX: touchX,
                startY: e.touches[0].clientY,
                timer: setTimeout(activate, LONG_PRESS_MS),
            };
        };

        const onTouchMove = (e) => {
            const g = gestureRef.current;

            // Long-press öncesi: hareket eşiğini aşarsa iptal, native scroll devam
            if (!g) {
                if (!pending || e.touches.length !== 1) return;
                const dx = e.touches[0].clientX - pending.startX;
                const dy = e.touches[0].clientY - pending.startY;
                if (dx * dx + dy * dy > MOVE_TOLERANCE_PX * MOVE_TOLERANCE_PX) {
                    clearPending();
                }
                return;
            }

            if (e.touches.length !== 1) return;
            if (e.cancelable) e.preventDefault();

            const y = e.touches[0].clientY;

            if (g.mode === 'move') {
                const deltaPx = y - g.initialTouchY;
                const deltaMs = snap(deltaPx * g.msPerPx);
                g.newStart = g.origStart + deltaMs;
                g.newEnd = g.origEnd + deltaMs;

                // Yatay: parmağın üzerinde olduğu birim sütununu bul → farklı birime taşı.
                // .rbc-day-slot kolonları resources sırasıyla birebir eşleşir.
                const x = e.touches[0].clientX;
                const columns = root.querySelectorAll('.rbc-time-content .rbc-day-slot');
                let targetResourceId = g.origResourceId;
                let colLeft = g.rectLeft;
                let colWidth = g.rectWidth;
                for (let i = 0; i < columns.length; i++) {
                    const r = columns[i].getBoundingClientRect();
                    if (x >= r.left && x <= r.right) {
                        const resId = resourcesRef.current[i]?.resourceId;
                        if (resId != null) {
                            targetResourceId = resId;
                            colLeft = r.left;
                            colWidth = r.width;
                        }
                        break;
                    }
                }
                g.newResourceId = targetResourceId;

                setTouchPreview({
                    mode: 'move',
                    top: g.rectTop + deltaPx,
                    left: colLeft,
                    width: colWidth,
                    height: g.rectHeight,
                    label: fmtRange(g.newStart, g.newEnd),
                    activated: true,
                });
                return;
            }

            // resize: sadece bitiş zamanını değiştir.
            // Parmak yukarı (deltaY < 0) → bitiş eskiye gider (kart kısalır)
            // Parmak aşağı (deltaY > 0) → bitiş ileriye gider (kart uzar)
            // Min 15 dk korunur. Start zamanı asla değişmez.
            const deltaMs = snap((y - g.initialTouchY) * g.msPerPx);
            g.newStart = g.origStart;
            g.newEnd = Math.max(g.origStart + MIN_DURATION_MS, g.origEnd + deltaMs);

            const newHeightPx = Math.max(8, (g.newEnd - g.newStart) / g.msPerPx);
            setTouchPreview({
                mode: 'resize',
                top: g.rectTop,
                left: g.rectLeft,
                width: g.rectWidth,
                height: newHeightPx,
                label: fmtRange(g.newStart, g.newEnd),
                activated: true,
            });
        };

        const finish = (e) => {
            const isCancel = e && e.type === 'touchcancel';
            // Long-press aktive olmadan touchend gelirse, bu bir tap'tir → onSelectEvent fire
            const tapAppointment = pending?.appointment;
            clearPending();
            const g = gestureRef.current;
            gestureRef.current = null;
            setTouchPreview(null);

            // Aktivasyon olmadan biten kısa basış: touchcancel ise tap sayılmaz
            if (!g) {
                if (!isCancel && tapAppointment) {
                    onSelectEvent(tapAppointment);
                }
                return;
            }

            const changed = g.newStart !== g.origStart || g.newEnd !== g.origEnd || g.newResourceId !== g.origResourceId;
            if (!changed || isCancel) return;

            const start = new Date(g.newStart);
            const end = new Date(g.newEnd);

            if (g.mode === 'move') {
                moveEvent({ event: g.appointment, start, end, resourceId: g.newResourceId });
            } else {
                resizeEvent({ event: g.appointment, start, end });
            }
        };

        // Chrome mobile / Android long-press context-menu'sünü engelle.
        const onContextMenu = (e) => {
            const el = e.target.closest && e.target.closest('.rbc-event');
            if (el) e.preventDefault();
        };

        // touchstart'ı yine de non-passive bırakıyoruz (ileride gerekirse), ama artık
        // preventDefault çağırmıyoruz; böylece kart üzerinden başlayan native scroll çalışır.
        root.addEventListener('touchstart', onTouchStart, { capture: true, passive: false });
        root.addEventListener('touchmove', onTouchMove, { capture: true, passive: false });
        root.addEventListener('touchend', finish, { capture: true, passive: true });
        root.addEventListener('touchcancel', finish, { capture: true, passive: true });
        root.addEventListener('contextmenu', onContextMenu, { capture: true });
        return () => {
            root.removeEventListener('touchstart', onTouchStart, { capture: true });
            root.removeEventListener('touchmove', onTouchMove, { capture: true });
            root.removeEventListener('touchend', finish, { capture: true });
            root.removeEventListener('touchcancel', finish, { capture: true });
            root.removeEventListener('contextmenu', onContextMenu, { capture: true });
            clearPending();
            gestureRef.current = null;
            setTouchPreview(null);
        };
    }, [isMobile, filteredAppointments]);

    return (
        <>
            <MobileView>
                <style>
                    {`
                        .rbc-timeslot-group {
                            min-height: 14px !important;
                        }
                        .rbc-time-slot {
                            min-height: 14px !important;
                            height: 14px !important;
                            line-height: 14px !important;
                            font-size: 10px;
                        }
                        .rbc-event {
                            padding: 0px 2px !important;
                            font-size: 9px !important;
                            min-height: 14px !important;
                            -webkit-touch-callout: none;
                            -webkit-user-select: none;
                            user-select: none;
                            -webkit-user-drag: none;
                            /* %75 noktasında ince ipucu çizgisi: sol = RESIZE, sağ %25 = MOVE */
                            background-image: linear-gradient(to right, transparent calc(75% - 1px), rgba(255,255,255,0.35) calc(75% - 1px), rgba(255,255,255,0.35) 75%, transparent 75%);
                        }
                        .rbc-event-label {
                            display: none;
                        }
                        .rbc-header {
                            font-size: 10px !important;
                            padding: 2px !important;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            white-space: nowrap;
                        }
                        .rbc-time-gutter {
                            width: 50px !important;
                            min-width: 50px !important;
                            max-width: 50px !important;
                            font-size: 11px !important;
                            /* Yatay scroll'da saat sütunu hep solda sabit kalsın */
                            position: sticky !important;
                            left: 0 !important;
                            z-index: 5 !important;
                            background: #fff !important;
                        }
                        .rbc-timeslot-group {
                            padding: 0 4px;
                        }
                        .rbc-time-header-gutter {
                            width: 50px !important;
                            min-width: 50px !important;
                            max-width: 50px !important;
                            /* Header'daki saat sütunu köşesi de sabit + en üstte */
                            position: sticky !important;
                            left: 0 !important;
                            z-index: 6 !important;
                            background: #fff !important;
                        }
                        /* Mobilde takvim kendi içinde scroll etmesin; sayfa ile uzasın */
                        .rbc-time-view,
                        .rbc-time-content {
                            overflow: visible !important;
                            flex: none !important;
                            height: auto !important;
                            max-height: none !important;
                        }
                        /* time-view ve resource wrapper'ı tüm içerik genişliğine uzasın.
                           Aksi halde sticky gutter'ın containing block'u viewport'a sınırlı
                           kalıp, container bitince saat sütunu da kayboluyor. */
                        .rbc-time-view,
                        .rbc-time-view-resources,
                        .rbc-time-content {
                            min-width: max-content !important;
                        }
                        /* Header de body kadar geniş olsun — yatay scroll'da sütun isimleri
                           sağ tarafta da görünsün (resimde sarı ile işaretli sorun) */
                        .rbc-time-header {
                            overflow: visible !important;
                            flex: none !important;
                            border-top: 1px solid #ddd !important;
                        }
                        .rbc-time-header-content {
                            flex: none !important;
                            min-width: max-content !important;
                            overflow: visible !important;
                        }
                        .rbc-time-header > .rbc-row,
                        .rbc-time-header-content > .rbc-row {
                            min-width: max-content !important;
                        }
                        /* Header cell'lerinde de görünür ayraç */
                        .rbc-time-header-content .rbc-header {
                            border-bottom: 1px solid #ddd !important;
                        }
                    `}
                </style>

                <div
                    ref={mobileRootRef}
                    style={{
                        // Yükseklik sabit değil — içerik kadar uzar, sayfa scroll eder.
                        // Horizontal scroll için sadece x ekseninde overflow auto.
                        overflowX: 'auto',
                        overflowY: 'visible',
                    }}
                >
                    <div style={{ minWidth: '100%' }}>
                        <DnDCalendar
                            {...limits}
                            formats={formats}
                            date={new Date(filter.date.local().toDate())}
                            defaultView={Views.DAY}
                            events={filteredAppointments}
                            localizer={localizer}
                            onSelectSlot={handleSelectSlot}
                            resources={resources}
                            resourceIdAccessor="resourceId"
                            resourceTitleAccessor="resourceName"
                            scrollToTime={scrollToTime}
                            selectable={true}
                            step={15}
                            timeslots={1}
                            toolbar={false}
                            onSelectEvent={onSelectEvent}
                            selected={selectedAppointment}
                            onNavigate={() => { }}
                            components={mobileCalendarComponents}
                            eventPropGetter={(event, start, end, isSelected) => ({
                                event,
                                start,
                                end,
                                isSelected,
                                style: { backgroundColor: getEventColor(event), fontSize: 9 }
                            })}
                        />
                    </div>
                </div>
                {touchPreview && (() => {
                    const isMove = touchPreview.mode === 'move';
                    const color = isMove ? '#1890ff' : '#52c41a';
                    const bg = isMove ? 'rgba(24,144,255,0.35)' : 'rgba(82,196,26,0.35)';
                    const modeLabel = isMove ? t('MOVE') : t('RESIZE');
                    return (
                        <>
                            <div
                                style={{
                                    position: 'fixed',
                                    top: touchPreview.top,
                                    left: touchPreview.left,
                                    width: touchPreview.width,
                                    height: touchPreview.height,
                                    background: bg,
                                    border: `2px solid ${color}`,
                                    borderRadius: 4,
                                    pointerEvents: 'none',
                                    zIndex: 9999,
                                    boxShadow: `0 0 0 4px ${color}33, 0 4px 16px rgba(0,0,0,0.25)`,
                                }}
                            >
                                <div style={{
                                    position: 'absolute',
                                    top: touchPreview.height + 6,
                                    left: 0,
                                    background: color,
                                    color: '#fff',
                                    padding: '2px 6px',
                                    borderRadius: 3,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                                }}>
                                    {touchPreview.label}
                                </div>
                            </div>
                            {/* Üstte sabit, çok belirgin mod uyarısı */}
                            <div style={{
                                position: 'fixed',
                                top: 12,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                background: color,
                                color: '#fff',
                                padding: '8px 16px',
                                borderRadius: 20,
                                fontSize: 13,
                                fontWeight: 700,
                                letterSpacing: 1,
                                zIndex: 10000,
                                pointerEvents: 'none',
                                boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
                            }}>
                                {modeLabel}
                            </div>
                        </>
                    );
                })()}
            </MobileView>
            <BrowserView>

                <div
                    style={{
                        height: height || window.innerHeight - 250
                    }}
                >
                    <DnDCalendar
                        {...limits}
                        formats={formats}
                        date={new Date(filter.date.local().toDate())}
                        defaultView={Views.DAY}
                        events={filteredAppointments}
                        localizer={localizer}
                        onEventDrop={moveEvent}
                        onEventResize={resizeEvent}
                        onSelectSlot={handleSelectSlot}
                        // draggableAccessor={() => true}   // hiçbir event sürüklenemez
                        resizable={true}
                        resources={resources}
                        resourceIdAccessor="resourceId"
                        resourceTitleAccessor="resourceName"
                        scrollToTime={scrollToTime}
                        selectable={true}
                        step={15}
                        toolbar={false}
                        onSelectEvent={onSelectEvent}
                        selected={selectedAppointment}
                        components={desktopCalendarComponents}
                        eventPropGetter={(event, start, end, isSelected) => ({
                            event,
                            start,
                            end,
                            isSelected,
                            style: { backgroundColor: getEventColor(event) }
                        })}
                    />
                </div>
            </BrowserView>
            <Modal
                title={t('Edit Blocked Slot')}
                visible={!!editingBlockedSlot}
                onCancel={() => setEditingBlockedSlot(null)}
                destroyOnClose
                footer={null}
            >
                {editingBlockedSlot && (
                    <Form
                        layout="vertical"
                        initialValues={{
                            range: [moment(editingBlockedSlot.start), moment(editingBlockedSlot.end)],
                            description: editingBlockedSlot.title === t('Blocked Slot') ? '' : editingBlockedSlot.title
                        }}
                        onFinish={async (values) => {
                            const [start, end] = values.range;
                            const res = await ApiService.Appointment.UpdateBlockedSlot({
                                id: editingBlockedSlot.id,
                                startDate: start.toDate(),
                                endDate: end.toDate(),
                                description: values.description
                            });
                            if (res.success) {
                                refresh();
                                setEditingBlockedSlot(null);
                            }
                        }}
                    >
                        <Form.Item label={t('Date Range')} name="range" rules={[{ required: true, message: t('Required') }]}>
                            <DatePicker.RangePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item label={t('Description')} name="description">
                            <Input placeholder={t('Description')} />
                        </Form.Item>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                            <Button
                                danger
                                onClick={async () => {
                                    const res = await ApiService.Appointment.UnblockSlot(editingBlockedSlot.id);
                                    if (res.success) {
                                        refresh();
                                        setEditingBlockedSlot(null);
                                    }
                                }}
                            >
                                {t('Delete')}
                            </Button>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <Button onClick={() => setEditingBlockedSlot(null)}>{t('Cancel')}</Button>
                                <Button type="primary" htmlType="submit">{t('Save')}</Button>
                            </div>
                        </div>
                    </Form>
                )}
            </Modal>
        </>
    )
}
